import { usePermisos } from "../../context/PermisosContext";
// src/components/Efectores/Efectores.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import TableroEfectores from "./TableroEfectores";
import CrearEfector from "./CrearEfector";
import AdministrarEfectores from "./AdministrarEfectores";

/* =============== helpers de permisos =============== */


// Requisitos por pestaña
const TAB_REQUIRE = {
  tablero: ["EFECTORES_TAB_TABLERO_VIEW"],
  crear: ["EFECTORES_TAB_CREAR_VIEW", "EFECTORES_TAB_CREAR_EDIT"],
  administrar: ["EFECTORES_TAB_EDITAR_VIEW"],
};

const ALL_TABS = [
  { id: "tablero", label: "Tablero de control" },
  { id: "administrar", label: "Administrar Efectores" },
  { id: "crear", label: "Crear Efector" },
];

/* =============== componente =============== */
const Efectores = ({ initialSub, onSubChange }) => {
  const { can: hasBit } = usePermisos();
  
  const [searchParams, setSearchParams] = useSearchParams();

  // Gate global (la ruta ya lo protege, esto es extra robustez)
  const canViewWindow = hasBit("EFECTORES_VIEW") ?? true;

  // Pestañas permitidas por bitset
  const visibleTabs = useMemo(() => {
    const allowIfUndefined = (keys) =>
      (keys || []).every((k) => {
        const v = hasBit(k);
        return v === true;
      });
    return ALL_TABS.filter((t) => allowIfUndefined(TAB_REQUIRE[t.id]));
  }, [hasBit]);

  // --- fuente de verdad del "sub" ---
  const subFromUrl = searchParams.get("sub") || "";

  const computeValidTab = (candidate) => {
    if (!candidate) return visibleTabs[0]?.id || null;
    const ok = visibleTabs.some((t) => t.id === candidate);
    return ok ? candidate : visibleTabs[0]?.id || null;
  };

  const initialTab = computeValidTab(subFromUrl || initialSub || "tablero");

  const [tab, setTab] = useState(initialTab);

  // Recalcular cuando cambia permisos/URL/prop inicial
  useEffect(() => {
    const next = computeValidTab(subFromUrl || initialSub || tab);
    if (next !== tab) setTab(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTabs, subFromUrl, initialSub]);

  // Espejo estado -> URL (y avisar al padre), evitando loops
  useEffect(() => {
    if (!tab) return;
    const next = new URLSearchParams(searchParams);
    if (next.get("sub") !== tab) {
      next.set("sub", tab);
      setSearchParams(next, { replace: true });
    }
    onSubChange?.(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  if (!canViewWindow) {
    return (
      <div className="bg-neutral-900 text-white p-2 sm:p-6 min-w-0 rounded-lg w-full min-h-[50dvh] md:min-h-screen">
        <h1 className="text-center text-2xl sm:text-3xl font-bold text-green-500 mb-4 sm:mb-6">
          GESTIÓN DE EFECTORES
        </h1>
        <div className="bg-neutral-800 border border-neutral-700 rounded p-4 text-center">
          <p className="text-sm text-neutral-300">No tienes permiso para ver esta sección.</p>
        </div>
      </div>
    );
  }

  if (!tab || visibleTabs.length === 0) {
    return (
      <div className="bg-neutral-900 text-white p-2 sm:p-6 min-w-0 rounded-lg w-full min-h-[50dvh] md:min-h-screen">
        <h1 className="text-center text-2xl sm:text-3xl font-bold text-green-500 mb-4 sm:mb-6">
          GESTIÓN DE EFECTORES
        </h1>
        <div className="bg-neutral-800 border border-neutral-700 rounded p-4 text-center">
          <p className="text-sm text-neutral-300">
            No hay pestañas disponibles con tus permisos actuales.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-neutral-900 text-white p-2 sm:p-6 min-w-0 rounded-lg w-full min-h-[50dvh] md:min-h-screen">
      <h1 className="text-center text-2xl sm:text-3xl font-bold text-green-500 mb-4 sm:mb-6">
        GESTIÓN DE EFECTORES
      </h1>

      {/* Tabs (solo las permitidas) */}
      <div className="mb-4 sm:mb-6 flex justify-center">
        <div className="grid grid-cols-1 sm:grid-cols-3 w-full gap-2">
          {visibleTabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`w-full min-h-12 px-3 sm:px-4 py-2 rounded text-sm sm:text-base transition-all duration-200 ${
                tab === t.id
                  ? "bg-green-500 text-black"
                  : "bg-gray-700 text-white hover:bg-gray-600"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 sm:mt-6">
        {tab === "tablero" && <TableroEfectores />}
        {tab === "administrar" && <AdministrarEfectores />}
        {tab === "crear" && <CrearEfector />}
      </div>
    </div>
  );
};

export default Efectores;
